import { CSSProperties, useCallback, useRef, useState } from "react";

import { Logo, PillLink } from "./components";
import { easeInOut, reducedMotion, span, useAnchors, usePlayer, useScrollScene, useTicker, useTimeline } from "./motion";

/** Overridable so a dev build's signup button does not reach production. */
const ADMIN_URL =
  ((import.meta as any).env?.VITE_PANEL_BASE_URL as string | undefined)?.replace(/\/+$/, "") ||
  "https://painel.fluxvia.com.br";

/** Also `SIGNUP_PATH` in crm-web and the tail of the invite link the API mints. */
const SIGNUP_URL = `${ADMIN_URL}/criar-conta`;

function Check({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Arrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="4" width="4.5" height="16" rx="1.2" />
      <rect x="13.5" y="4" width="4.5" height="16" rx="1.2" />
    </svg>
  );
}

function ReplayIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

/** Meta's own mark, at its published geometry.
 *
 *  The path is the brand's mark as it ships in simple-icons (source
 *  `meta.com`), not a redraw. A credential loses the thing it is for the moment
 *  the logo beside it is an approximation, so this is the one drawing on the
 *  page nobody may adjust to taste. Colour comes from `.cred` rather than from
 *  here so the mark stays a shape and the palette stays in one place. */
function MetaMark() {
  return (
    <svg className="cred-mark meta" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" />
    </svg>
  );
}

/** WhatsApp's own mark. Same source and same rule as {@link MetaMark}: the
 *  geometry is theirs, not ours. Sized by the caller's class. */
function WhatsAppMark({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

/** The two credential cards. `docs/mockups/landing-credencial-meta.png` drew
 *  them and is historical reference now; what each card may CLAIM is owned by
 *  `docs/meta-app-review.md`. The WhatsApp card reads `Integração oficial`
 *  where the drawing reads `Parceiro oficial`. */
function CredentialCards() {
  return (
    <div className="cred" role="group" aria-label="Credenciais da plataforma">
      <div className="cred-card">
        <div className="cred-brand">
          <MetaMark />
          <span className="cred-word">Meta</span>
        </div>
        <div className="cred-foot">
          <span className="cred-role">Tech Provider</span>
          <span className="cred-seal">Verificado</span>
        </div>
      </div>
      <div className="cred-card cred-card-wa">
        <WhatsAppMark className="cred-mark wa" />
        <div className="cred-txt">
          <div className="cred-role">Integração oficial</div>
          <div className="cred-plat">WhatsApp Business Platform</div>
        </div>
      </div>
    </div>
  );
}

export function Nav() {
  return (
    <nav className="nav">
      <div className="nav-inner">
        <a href="#topo" className="nav-brand" aria-label="Fluxvia">
          <Logo size={26} />
        </a>
        <div className="nav-links">
          <a href="#produto">Como funciona</a>
          <a href="#resolve">O que resolve</a>
          <a href="#integracoes">Integrações</a>
          <a href="#planos">Planos</a>
          <a href="/sua-carteira.html">Sua carteira</a>
        </div>
        <div className="nav-ctas">
          <a className="nav-login" href={ADMIN_URL}>
            Entrar
          </a>
          <PillLink href={SIGNUP_URL}>Criar minha conta</PillLink>
        </div>
      </div>
    </nav>
  );
}

/** A line whose words rise out of their own baseline one after another when
 *  the nearest scene turns `on` (or reaches the step its class names). The
 *  spaces stay text nodes, so the line reads and copies as one sentence. */
function Words({ text, delay = 0 }: { text: string; delay?: number }) {
  const words = text.split(" ");
  return (
    <span className="kw" style={{ "--d": `${delay}ms` } as CSSProperties}>
      {words.map((w, i) => (
        <span key={i}>
          {i > 0 && " "}
          <span className="w">
            <span className="wi" style={{ "--i": i } as CSSProperties}>
              {w}
            </span>
          </span>
        </span>
      ))}
    </span>
  );
}

/* ================= the product, as one window ============================
   The same window is drawn in three places: the hero, where it grows until
   the screen is the product and a conversation plays in it; the moment it is
   seen whole; and, in parts, beside the words that name what it does. Every
   element carries its own resting state; the scene's step classes (`s1`,
   `s2`, ...) move it, and the CSS decides how. Positions are in the stage's
   own pixels, so the timings and the travel are the same on every screen. */

const CONVS: Array<[string, string, string, string]> = [
  ["r", "Roberto Lima", "Obrigado, até amanhã!", "22:10"],
  ["j", "Júlia Prado", "Quero marcar uma avaliação", "21:32"],
  ["m", "Marina Alves", "Oi! Surgiu um imprevisto...", "23:47"],
  ["c", "Carlos Souza", "Pode ser às 9h?", "19:05"],
  ["p", "Patrícia Nunes", "Perfeito, confirmado", "18:40"],
];

function WindowFrame() {
  return (
    <>
      <div className="chrome">
        <span className="dots">
          <i />
          <i />
          <i />
        </span>
        <span className="win-title">Fluxvia</span>
      </div>
      <div className="rail">
        {[0, 1, 2, 3, 4].map((i) => (
          <i key={i} className={i === 0 ? "on" : undefined} />
        ))}
      </div>
      <div className="convs region-chat">
        <div className="convs-h">Conversas</div>
        <div className="conv-list">
          {CONVS.map(([key, name, last, when]) => (
            <div key={key} className={`conv conv-${key}`}>
              <span className="conv-av">{name[0]}</span>
              <span className="conv-t">
                <b>{name}</b>
                {key === "m" ? (
                  <span className="pv">
                    <span className="pv-old">Até amanhã às 14h!</span>
                    <span className="pv-new">{last}</span>
                  </span>
                ) : (
                  <span>{last}</span>
                )}
              </span>
              <span className="conv-w">
                {when}
                {key === "m" && <i className="unread" />}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Chat() {
  return (
    <div className="card chat region-chat">
      <div className="chat-h">
        <span className="chat-av">M</span>
        <span className="chat-who">
          <b>Marina Alves</b>
          <span>pelo WhatsApp</span>
        </span>
        <WhatsAppMark className="chat-wa" />
      </div>
      <div className="chat-body">
        <span className="typing typing-in">
          <i />
          <i />
          <i />
        </span>
        <div className="msg msg-in m1">
          Oi! Surgiu um imprevisto, não vou conseguir ir amanhã às 14h.
          <span className="stamp">23:47</span>
        </div>
        <div className="say m2">
          <span className="typing">
            <i />
            <i />
            <i />
          </span>
          <div className="msg msg-out">
            Sem problema. Tenho <span className="fact">sexta às 10h</span> ou{" "}
            <span className="fact">segunda às 16h</span> com o Rafael. Qual fica melhor?
          </div>
        </div>
        <div className="msg msg-in m3">sexta 10h</div>
        <div className="say m4">
          <div className="msg msg-out">
            Remarcado para sexta às 10h com o Rafael.{" "}
            <span className="fact" data-anchor="f-rem">
              Te lembro na quinta.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Agenda() {
  return (
    <div className="card agenda region-agenda">
      <div className="panel-h">
        <span className="panel-t">Agenda do Rafael</span>
        <span className="sync">atualizada</span>
      </div>
      <div className="slot slot-a">
        <span className="slot-time">Amanhã 14:00</span>
        <span className="slot-body">
          <span className="who who-marina-old" data-anchor="s-old">
            Marina
          </span>
          <span className="freed">vaga liberada</span>
          <span className="who who-bruno" data-anchor="s-bruno">
            Bruno, da espera
          </span>
        </span>
      </div>
      <div className="slot">
        <span className="slot-time">Sexta 09:00</span>
        <span className="slot-body">
          <span className="who">Rodrigo</span>
        </span>
      </div>
      <div className="slot slot-c">
        <span className="slot-time">Sexta 10:00</span>
        <span className="slot-body">
          <span className="free">livre</span>
          <span className="who who-marina-new" data-anchor="s-new">
            Marina
          </span>
        </span>
      </div>
      <div className="slot">
        <span className="slot-time">Sexta 11:00</span>
        <span className="slot-body">
          <span className="who">Ana</span>
        </span>
      </div>
      <div className="slot slot-e">
        <span className="slot-time">Segunda 16:00</span>
        <span className="slot-body">
          <span className="free">livre</span>
        </span>
      </div>
    </div>
  );
}

function Waitlist() {
  return (
    <div className="card waitlist region-agenda">
      <div className="panel-h">
        <span className="panel-t">Lista de espera</span>
        <span className="panel-s">amanhã à tarde</span>
      </div>
      <div className="wait wait-bruno">
        <span className="conv-av">B</span>
        <span className="wait-name" data-anchor="w-bruno">
          Bruno Castro
        </span>
        <span className="wait-state">oferecida</span>
      </div>
      <div className="wait">
        <span className="conv-av">L</span>
        <span className="wait-name">Luana Reis</span>
      </div>
      <div className="wait">
        <span className="conv-av">T</span>
        <span className="wait-name">Tiago Melo</span>
      </div>
    </div>
  );
}

const TODAY = [
  ["t1", "Remarcação de Marina"],
  ["t2", "Lembrete marcado para quinta"],
  ["t3", "Vaga de amanhã oferecida à lista de espera"],
];

function Today() {
  return (
    <div className="card today region-today">
      <div className="panel-h">
        <span className="panel-t">Hoje</span>
        <span className="panel-s">feito pela Fluxvia</span>
      </div>
      {TODAY.map(([id, text]) => (
        <div key={id} className={`done done-${id}`} data-anchor={id}>
          <span className="done-chk">
            <Check size={11} />
          </span>
          {text}
        </div>
      ))}
    </div>
  );
}

/** The whole window, at rest or playing, depending on the scene around it. */
function ProductWindow() {
  return (
    <>
      <WindowFrame />
      <Chat />
      <Agenda />
      <Waitlist />
      <Today />
    </>
  );
}

/* ================= SCENE 1 — enter the product, watch it work =============
   The hero is a headline and the Fluxvia window beside it, alive: the
   conversations of the whole base flow in from the edge of the page and
   converge into it. Scrolling carries the window forward until the screen is
   the product; that growth is the one motion tied to the scroll. Once inside,
   a conversation plays by itself and the visitor only watches: Marina writes,
   the Fluxvia checks the agenda before answering, the appointment moves, the
   freed slot goes to the waiting list, and each action becomes a line of Hoje. */

/** When each step of the conversation starts, in ms after the visitor is in. */
const PLAY_AT = [300, 1700, 3500, 5200, 6300, 7600, 8700, 9700, 11100, 12500, 13300, 13900, 14500];
/** How long the conversation runs, with a moment on the finished Hoje. */
const PLAY_TOTAL = 15800;
/** The chapters the player offers, as `[label, index of the step it starts]`. */
const CHAPTERS: Array<[string, number]> = [
  ["Mensagem", 0],
  ["Consulta", 1],
  ["Resposta", 2],
  ["Remarca", 5],
  ["Lista de espera", 7],
  ["Hoje", 9],
];

/** The player's bar gives every chapter the same width and fills each one
 *  over its own duration, so short chapters stay readable on the bar. */
function chapterProgress(ms: number): number {
  const starts = [...CHAPTERS.map(([, i]) => PLAY_AT[i]), PLAY_TOTAL];
  const n = CHAPTERS.length;
  for (let k = n - 1; k >= 0; k--) {
    if (ms >= starts[k]) return (k + Math.min(1, (ms - starts[k]) / (starts[k + 1] - starts[k]))) / n;
  }
  return 0;
}
/** Where the growth ends: past it the conversation plays; back before the
 *  second value it resets, so the hero is calm again. */
const INSIDE_AT: [number, number] = [0.62, 0.3];
/** Where the hero copy has gone: past it, it leaves the tab order. */
const COPY_GONE = 0.25;

/** Where the stage sits in the hero and inside, as a screen point for a stage
 *  point and a scale. The growth interpolates between the two. */
type Place = { x: number; y: number; fx: number; fy: number; s: number };

function places(hero: HTMLElement | null): { from: Place; to: Place; phone: boolean } {
  const vw = window.innerWidth;
  const vh = document.documentElement.clientHeight;
  const phone = vw < 1040;
  if (phone) {
    const foot = hero?.querySelector<HTMLElement>(".hero-foot");
    const bottom = foot && hero ? hero.offsetTop + foot.offsetTop + foot.offsetHeight : vh * 0.6;
    const s0 = ((vw - 40) / 360) * 0.9;
    const s1 = (vw - 20) / 360;
    return {
      phone,
      from: { x: vw / 2, y: bottom + 28 + 180 * s0, fx: 180, fy: 180, s: s0 },
      to: { x: vw / 2, y: (vh + 64) / 2, fx: 180, fy: 330, s: s1 },
    };
  }
  const col = Math.max(20, (vw - 1320) / 2 + 48);
  /* Beside the copy, never over it: the window starts where the widest of
     the headline and the text under it ends. */
  const edge = (sel: string) => {
    const el = hero?.querySelector<HTMLElement>(sel);
    return el && hero ? hero.offsetLeft + el.offsetLeft + el.offsetWidth : vw * 0.5;
  };
  const copyRight = Math.max(edge(".hero-title"), edge(".hero-foot"));
  /* The hero shows the first 780 stage pixels (list and conversation), as
     large as the room beside the copy and the screen's height allow. */
  const w = Math.min(Math.max(vw - col - copyRight - 48, 420), 900, ((vh - 64 - 56) * 780) / 800);
  const s0 = w / 780;
  const s1 = Math.min(1.3, vw / 1000, (vh - 64) / 600);
  return {
    phone,
    from: { x: vw - col - w / 2, y: (vh + 64) / 2, fx: 390, fy: 400, s: s0 },
    to: { x: vw / 2, y: (vh + 64) / 2, fx: 560, fy: 360, s: s1 },
  };
}

/** The lines the base's conversations travel along in the hero: from the
 *  page's left edge, fanned, into one point just outside the window, and from
 *  there as one line into it. */
function threadPaths(from: Place): string[] {
  const vh = document.documentElement.clientHeight;
  const left = from.x - from.fx * from.s;
  const ty = from.y + (190 - from.fy) * from.s;
  const cx = left - 40;
  const n = 15;
  return Array.from({ length: n }, (_, i) => {
    const y = vh * (0.04 + (0.92 * i) / (n - 1));
    return `M -40 ${y.toFixed(1)} C ${(cx * 0.52).toFixed(1)} ${y.toFixed(1)}, ${(cx * 0.62).toFixed(1)} ${ty.toFixed(1)}, ${cx.toFixed(1)} ${ty.toFixed(1)} L ${(left + 6).toFixed(1)} ${ty.toFixed(1)}`;
  });
}

export function SceneProduct() {
  const ref = useRef<HTMLElement>(null);
  const copy = useRef<HTMLDivElement>(null);
  const [inside, setInside] = useState(false);
  const [controls, setControls] = useState(false);
  const [paths, setPaths] = useState<string[]>([]);
  const lastPaths = useRef("");

  const onProgress = useCallback((p: number) => {
    const el = ref.current;
    if (!el) return;
    if (copy.current) copy.current.inert = p > COPY_GONE;
    const { from, to, phone } = places(copy.current);
    const g = reducedMotion() ? (p > INSIDE_AT[0] ? 1 : 0) : easeInOut(span(p, 0, INSIDE_AT[0]));
    const mix = (a: number, b: number) => a + (b - a) * g;
    el.style.setProperty("--g", g.toFixed(4));
    el.style.setProperty("--gx", mix(from.x, to.x).toFixed(2));
    el.style.setProperty("--gy", mix(from.y, to.y).toFixed(2));
    el.style.setProperty("--gfx", mix(from.fx, to.fx).toFixed(2));
    el.style.setProperty("--gfy", mix(from.fy, to.fy).toFixed(2));
    el.style.setProperty("--gs", mix(from.s, to.s).toFixed(4));
    if (p >= INSIDE_AT[0]) setInside(true);
    else if (p < INSIDE_AT[1]) setInside(false);
    /* The controls take input exactly while they can be seen. */
    setControls(p >= INSIDE_AT[0]);
    const next = phone ? [] : threadPaths(from);
    const key = next.join("|");
    if (key !== lastPaths.current) {
      lastPaths.current = key;
      setPaths(next);
    }
  }, []);
  useScrollScene(ref, onProgress);
  useAnchors(ref);
  const bar = useRef<HTMLDivElement>(null);
  const player = usePlayer(ref, PLAY_AT, PLAY_TOTAL, inside, bar, chapterProgress);
  const chapter = CHAPTERS.filter(([, i]) => player.step > i).length - 1;

  return (
    <section ref={ref} className="scene scene-a" id="topo" aria-label="A Fluxvia atendendo uma conversa">
      <div className="pin">
        <svg className="threads" aria-hidden>
          {paths.map((d, i) => (
            <path key={i} className="thread" d={d} />
          ))}
        </svg>
        <div className="sparks" aria-hidden>
          {paths.map((d, i) => (
            <i
              key={i}
              className={i % 4 === 0 ? "spark big" : "spark"}
              style={
                {
                  offsetPath: `path("${d}")`,
                  animationDelay: `${((i * 0.73) % 5).toFixed(2)}s`,
                  animationDuration: `${(5 + (i % 5) * 0.7).toFixed(1)}s`,
                } as CSSProperties
              }
            />
          ))}
        </div>

        <div ref={copy} className="hero">
          <h1 className="hero-title">
            <Words text="Sua equipe entra só quando precisa." />
          </h1>
          <div className="hero-foot">
            <p className="hero-sub">
              Uma recepção que reativa, qualifica e fecha em cima da sua própria base. Você vê quem
              respondeu, quem agendou e tudo o que a Fluxvia fez.
            </p>
            <div className="hero-ctas">
              <a className="cta-primary" href={SIGNUP_URL}>
                Criar minha conta
                <Arrow />
              </a>
              <a className="cta-ghost" href="#produto">
                Ver a Fluxvia trabalhando
              </a>
            </div>
            <p className="hero-for">
              Para quem atende todo dia: clínicas, estética, fisioterapia, academias, salões,
              barbearias e corretoras.
            </p>
            <CredentialCards />
          </div>
        </div>

        <div className="inside-bg" aria-hidden />
        <div className="grow" aria-hidden>
          <div className="stage cam" data-canvas>
            <ProductWindow />
            <span className="fly fly-marina" data-fly="s-old>s-new">
              Marina
            </span>
            <span className="fly fly-bruno" data-fly="w-bruno>s-bruno">
              Bruno Castro
            </span>
            <span className="fly fly-rec fly-t1" data-fly="s-new>t1">
              <span className="from">Marina · sexta 10:00</span>
              <span className="to">Remarcação de Marina</span>
            </span>
            <span className="fly fly-rec fly-t2" data-fly="f-rem>t2">
              <span className="from">Te lembro na quinta.</span>
              <span className="to">Lembrete marcado para quinta</span>
            </span>
            <span className="fly fly-rec fly-t3" data-fly="s-bruno>t3">
              <span className="from">Bruno, da espera</span>
              <span className="to">Vaga de amanhã oferecida à lista de espera</span>
            </span>
          </div>
        </div>

        {/* The conversation is an animation, and says so: it can be paused,
            replayed or opened at a chapter, like a video. */}
        <div className="player" role="group" aria-label="Controles da demonstração" {...(controls ? {} : { inert: "" })}>
          <button
            type="button"
            className="pl-play"
            onClick={player.toggle}
            aria-label={player.playing ? "Pausar" : player.done ? "Ver de novo" : "Continuar"}
          >
            {player.playing ? <PauseIcon /> : player.done ? <ReplayIcon /> : <PlayIcon />}
          </button>
          <div className="pl-rail">
            <div ref={bar} className="pl-bar">
              <span className="pl-fill" />
            </div>
            <div className="pl-chapters">
              {CHAPTERS.map(([label, i], n) => (
                <button
                  key={label}
                  type="button"
                  className={"pl-ch" + (n === chapter ? " now" : "")}
                  style={{ "--x": n / CHAPTERS.length } as CSSProperties}
                  onClick={() => player.seek(i)}
                  aria-current={n === chapter ? "step" : undefined}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="cue" aria-hidden>
          <span className="cue-line" />
          Role para ver a Fluxvia trabalhar
        </div>
      </div>
      <span id="produto" className="anchor anchor-produto" />
    </section>
  );
}

/* ================= SCENE 2 — the base comes to life =======================
   An operation, running. On the left, the base in order of how long each
   client has been away. The Fluxvia takes them in turn: the client lights
   up, a conversation is born beside it, and what comes of it becomes a
   record that travels into the day's feed and moves the day's numbers. In
   between, the rest of the day keeps arriving in the feed on its own:
   confirmations, reschedules, questions answered. It runs while it is
   watched and rests at the end of the day, where the numbers are the sum of
   what went by. */

type Outcome = "agendou" | "respondeu" | "sem resposta";
type Client = { name: string; days: number; outcome: Outcome; hello: string; reply?: string; close?: string; record: string };

const CLIENTS: Client[] = [
  { name: "Roberto Lima", days: 120, outcome: "agendou", hello: "Oi, Roberto! Faz tempo que você não aparece. Tenho quinta às 9h com a Ana, quer?", reply: "quero sim", close: "Marcado: quinta às 9h com a Ana.", record: "Roberto Lima agendou quinta, 9h" },
  { name: "Marina Alves", days: 94, outcome: "respondeu", hello: "Oi, Marina! Sentimos sua falta por aqui. Posso ver um horário pra você?", reply: "vou ver minha semana e te falo", close: "Combinado. Te chamo na segunda.", record: "Marina Alves respondeu, retorno na segunda" },
  { name: "Carlos Souza", days: 61, outcome: "sem resposta", hello: "Oi, Carlos! Faz um tempo. Quer que eu reserve um horário essa semana?", record: "Carlos Souza chamado, sem resposta ainda" },
  { name: "Patrícia Nunes", days: 47, outcome: "agendou", hello: "Oi, Patrícia! Abriu um horário sexta às 16h. Quer?", reply: "pode ser!", close: "Marcado: sexta às 16h.", record: "Patrícia Nunes agendou sexta, 16h" },
  { name: "Júlia Prado", days: 33, outcome: "respondeu", hello: "Oi, Júlia! Quer voltar a cuidar de você essa semana?", reply: "qual o valor da sessão?", close: "Te mandei os valores da tabela. Quer marcar?", record: "Júlia Prado perguntou o valor e recebeu a tabela" },
  { name: "Renato Dias", days: 28, outcome: "agendou", hello: "Oi, Renato! Tenho sábado às 10h livre. Serve?", reply: "serve", close: "Marcado: sábado às 10h.", record: "Renato Dias agendou sábado, 10h" },
];

/** The rest of the day, arriving on its own between the base's turns:
 *  `[record, kind]`, where kind says which numbers it moves. */
const INBOUND: Array<[string, "confirmou" | "remarcou" | "agendou" | "respondeu"]> = [
  ["Ana Costa confirmou amanhã, 10h", "confirmou"],
  ["Lucas Rocha remarcou para segunda", "remarcou"],
  ["Bia Santos marcou uma avaliação", "agendou"],
  ["Pedro Alves perguntou o preço e recebeu a tabela", "respondeu"],
  ["Clara Mota remarcou para sábado", "remarcou"],
  ["Tomás Reis confirmou hoje, 18h", "confirmou"],
];

/** One beat of the operation: a base client's three moments, or one inbound
 *  record. Built once, so the scene is a list of beats to walk through. */
type Beat =
  | { kind: "client"; client: number; phase: 0 | 1 | 2 }
  | { kind: "inbound"; item: number };
const BEATS_OF_DAY: Beat[] = CLIENTS.flatMap((_, c) => {
  const turn: Beat[] = [0, 1, 2].map((phase) => ({ kind: "client", client: c, phase: phase as 0 | 1 | 2 }));
  return c < INBOUND.length ? [...turn, { kind: "inbound", item: c }] : turn;
});

/** The day's numbers before the loop starts; every record adds to them. */
const DAY_START = { agend: 34, conv: 101, remarc: 7 };

type Record = { id: string; text: string; kind: string };

/** Everything the operation has produced up to beat `upTo`: the feed's
 *  records, newest first, and the three numbers. Derived, not accumulated,
 *  so it is right at every beat and when the loop starts over. */
function dayUpTo(upTo: number): { records: Record[]; agend: number; conv: number; remarc: number } {
  const records: Record[] = [];
  let { agend, conv, remarc } = DAY_START;
  BEATS_OF_DAY.slice(0, upTo + 1).forEach((b, i) => {
    if (b.kind === "client" && b.phase === 2) {
      const c = CLIENTS[b.client];
      records.unshift({ id: `c${i}`, text: c.record, kind: c.outcome });
      if (c.outcome !== "sem resposta") conv += 1;
      if (c.outcome === "agendou") agend += 1;
    }
    if (b.kind === "inbound") {
      const [text, kind] = INBOUND[b.item];
      records.unshift({ id: `i${i}`, text, kind });
      conv += 1;
      if (kind === "agendou") agend += 1;
      if (kind === "remarcou") remarc += 1;
    }
  });
  return { records: records.slice(0, 6), agend, conv, remarc };
}

/** Where each base client stands at beat `upTo`. */
function clientState(c: number, upTo: number): "sem contato" | "chamando" | Outcome {
  const beat = BEATS_OF_DAY[upTo];
  const firstBeat = BEATS_OF_DAY.findIndex((b) => b.kind === "client" && b.client === c);
  if (upTo < firstBeat) return "sem contato";
  if (beat.kind === "client" && beat.client === c && beat.phase < 2) return "chamando";
  return CLIENTS[c].outcome;
}

function Counter({ value, label }: { value: number; label: string }) {
  return (
    <div className="op-count">
      <b key={value}>{value}</b>
      <span>{label}</span>
    </div>
  );
}

export function SceneBase() {
  const ref = useRef<HTMLElement>(null);
  const [tick] = useTicker(ref, 1250, BEATS_OF_DAY.length);
  const day = dayUpTo(tick);
  const beat = BEATS_OF_DAY[tick];
  /* Between the base's turns the last conversation stays in view, quieter,
     while the rest of the day arrives in the feed. */
  const current = BEATS_OF_DAY.slice(0, tick + 1).reverse().find((b) => b.kind === "client") as
    | { kind: "client"; client: number; phase: 0 | 1 | 2 }
    | undefined;
  const client = current ? CLIENTS[current.client] : null;
  return (
    <section ref={ref} className="op" aria-label="A Fluxvia chama quem sumiu">
      <div className="op-head">
        <div>
          <h2 className="op-title">Sua maior oportunidade está parada na sua base.</h2>
          <p className="op-sub">
            Cliente que não volta há 60+ dias, lead que perguntou preço e sumiu, aniversariante
            esquecido. A Fluxvia chama quem sumiu, e cada resposta vira registro no seu dia.
          </p>
        </div>
        <div className="op-counts" aria-live="off">
          <Counter value={day.agend} label="agendamentos criados" />
          <Counter value={day.conv} label="conversas respondidas" />
          <Counter value={day.remarc} label="remarcações" />
        </div>
      </div>

      <div className="op-board" aria-hidden>
        <div className="op-base">
          <div className="op-h">
            <b>Base de clientes</b>
            <span>quem sumiu há mais tempo primeiro</span>
          </div>
          {CLIENTS.map((c, i) => {
            const state = clientState(i, tick);
            return (
              <div key={c.name} className={"op-row" + (beat.kind === "client" && current?.client === i ? " live" : "")} style={{ "--w": c.days / 120 } as CSSProperties}>
                <span className="op-name">{c.name}</span>
                <span className="age-bar">
                  <span className="age-fill" />
                </span>
                <span className="op-days">{c.days} dias</span>
                <span className={`op-state st-${state.replace(" ", "-")}`}>{state}</span>
              </div>
            );
          })}
        </div>

        <div className="op-talk">
          {client && current && (
            <div
              key={client.name}
              className={"op-thread" + (beat.kind === "inbound" ? " past" : "")}
              style={{ "--row": current.client } as CSSProperties}
            >
              <div className="op-who">
                <span className="chat-av">{client.name[0]}</span>
                {client.name}
                <WhatsAppMark className="chat-wa" />
              </div>
              <div className="msg msg-out op-m">{client.hello}</div>
              {current.phase >= 1 &&
                (client.reply ? (
                  <div className="msg msg-in op-m">{client.reply}</div>
                ) : (
                  <div className="op-quiet">sem resposta ainda</div>
                ))}
              {current.phase >= 2 && client.close && <div className="msg msg-out op-m">{client.close}</div>}
              {current.phase >= 2 && <div className={`op-result r-${client.outcome.replace(" ", "-")}`}>{client.record}</div>}
            </div>
          )}
          {beat.kind === "inbound" && (
            <div key={`in${tick}`} className="op-inbound">
              <span className="op-pulse" />
              Enquanto isso, nas outras conversas
            </div>
          )}
        </div>

        <div className="op-feed">
          <div className="op-h">
            <b>Hoje</b>
            <span>feito pela Fluxvia</span>
          </div>
          {day.records.map((r) => (
            <div key={r.id} className={`op-rec k-${r.kind.replace(" ", "-")}`}>
              <span className="op-dot" />
              {r.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================= the channel, live ======================================
   A dark band that is the channel itself: messages from clients stream
   across it without pause, each one read as it goes by. The four figures of
   the channel sit on that stream in order of weight, the first one large,
   and each draws what it measures once the band is in view: 169 dots, one per
   million people; a ring at 96%; e-mail's one against WhatsApp's three to
   five; ten no-shows of which three become visits. */

const STREAM = [
  "Oi, tem horário amanhã?",
  "Pode ser às 15h?",
  "Quero remarcar",
  "Qual o valor da avaliação?",
  "Obrigada!",
  "Consigo sábado de manhã?",
  "Vocês atendem convênio?",
  "Chego em 10 minutos",
  "Pode confirmar pra mim?",
  "Faz tempo que não vou, tem vaga?",
];
const CHANNEL_AT = [200, 700, 1200, 1700];

function Lane({ items, reverse }: { items: string[]; reverse?: boolean }) {
  const doubled = [...items, ...items];
  return (
    <div className={"lane" + (reverse ? " lane-r" : "")}>
      <div className="lane-track">
        {doubled.map((t, i) => (
          <span key={i} className={"wa-b" + (i % 13 === 7 ? " unread" : "")}>
            {t}
            <svg className="ticks" viewBox="0 0 18 12" aria-hidden>
              <path d="M1 6.5 4.5 10 11 2.5" />
              <path d="M7 10 13.5 2.5" />
            </svg>
          </span>
        ))}
      </div>
    </div>
  );
}

export function WhyWhatsApp() {
  const ref = useRef<HTMLElement>(null);
  useTimeline(ref, CHANNEL_AT, { threshold: 0.3 });
  return (
    <section ref={ref} className="channel" aria-label="Por que conversa no WhatsApp">
      <div className="channel-band">
        <div className="channel-stream" aria-hidden>
          <Lane items={STREAM} />
          <Lane items={[...STREAM].reverse()} reverse />
          <Lane items={STREAM.slice(3).concat(STREAM.slice(0, 3))} />
        </div>
        <div className="channel-copy">
          <h2 className="channel-title">
            <Words text="É onde seu cliente já fala com você." />
          </h2>
          <div className="lead-fig fig-1">
            <Dots169 />
            <div>
              <div className="fig-n">169 mi</div>
              <div className="fig-l">brasileiros no WhatsApp</div>
            </div>
          </div>
        </div>
        <div className="channel-figs">
          <div className="fig fig-2">
            <Ring96 />
            <div>
              <div className="fig-n">~96%</div>
              <div className="fig-l">de abertura no WhatsApp</div>
            </div>
          </div>
          <div className="fig fig-3">
            <Bars35 />
            <div>
              <div className="fig-n">3–5×</div>
              <div className="fig-l">reativação converte vs. e-mail</div>
            </div>
          </div>
          <div className="fig fig-4">
            <Shows30 />
            <div>
              <div className="fig-n">–30%</div>
              <div className="fig-l">faltas com lembrete no dia anterior</div>
            </div>
          </div>
        </div>
        <p className="mk-foot">Dados de mercado do canal.</p>
      </div>
      {/* On phones the opening frame has no room for the credentials, so
          they land here, the first calm stretch after the scenes. */}
      <div className="wrap mobile-cred">
        <CredentialCards />
      </div>
    </section>
  );
}

function Dots169() {
  return (
    <div className="viz viz-dots" aria-hidden>
      {Array.from({ length: 169 }, (_, i) => (
        <i key={i} style={{ "--i": i } as CSSProperties} />
      ))}
    </div>
  );
}

function Ring96() {
  return (
    <svg className="viz viz-ring" viewBox="0 0 120 120" aria-hidden>
      <circle cx="60" cy="60" r="50" className="ring-track" />
      <circle cx="60" cy="60" r="50" className="ring-fill" pathLength={100} />
    </svg>
  );
}

function Bars35() {
  return (
    <div className="viz viz-bars" aria-hidden>
      <div className="vb">
        <span className="vb-k">E-mail</span>
        <span className="vb-track">
          <i className="vb-email" />
        </span>
      </div>
      <div className="vb">
        <span className="vb-k">WhatsApp</span>
        <span className="vb-track">
          <i className="vb-range" />
          <i className="vb-wa" />
        </span>
      </div>
    </div>
  );
}

function Shows30() {
  return (
    <div className="viz viz-shows" aria-hidden>
      {Array.from({ length: 10 }, (_, i) => (
        <i key={i} className={i % 3 === 1 && i < 9 ? "turn" : undefined} style={{ "--i": i } as CSSProperties} />
      ))}
    </div>
  );
}

/* ================= not another chatbot ====================================
   First the visitor lives through the bot they already know: a menu of
   numbers, a question it cannot take, "Opção inválida", a CPF asked for, the
   same error again. Then the same screen turns into the Fluxvia: the menu
   melts, the name changes, and the same question gets a plain answer that
   books the slot. Around the phone, each trait turns into its counterpart as
   it happens. The two buttons above it replay either side. */

const COMPARE = [
  { label: "Conversa", old: "Fluxo fixo, com botões", neu: "Linguagem natural" },
  { label: "Entendimento", old: "Palavra-chave", neu: "Entende intenção" },
  { label: "Integração", old: "Não conecta no seu sistema", neu: "Conecta com ERP, agenda, gateway" },
  { label: "Resolução", old: "Quase sempre passa pra humano", neu: "Resolve sozinha" },
  { label: "Configuração", old: "Você desenha cada caminho", neu: "Você descreve o negócio, a Fluxvia aprende" },
  { label: "Cliente sente", old: "Frustração", neu: "Atendimento rápido, sem cara de robô" },
];

/** The bot's side, then the Fluxvia's, as `[who, text]`. `menu` is the bot's
 *  numbered menu. The turn index where the Fluxvia takes over is MORPH_AT. */
const BOT_TURNS: Array<["bot" | "user", string]> = [
  ["bot", "menu"],
  ["user", "consigo remarcar pra sexta?"],
  ["bot", "Opção inválida. Digite 1, 2 ou 3."],
  ["user", "1"],
  ["bot", "Agendamento. Informe seu CPF."],
  ["user", "só quero passar pra sexta"],
  ["bot", "Opção inválida. Digite 1, 2 ou 3."],
];
const FLUX_TURNS: Array<["flux" | "user", string]> = [
  ["user", "consigo remarcar pra sexta?"],
  ["flux", "Consigo sim. Sexta às 10h com o Rafael está livre, marco pra você?"],
  ["user", "pode marcar"],
  ["flux", "Pronto: sexta às 10h. Te lembro na quinta."],
];
/** Beats: the bot's turns one per beat, one beat for the melt, the Fluxvia's
 *  turns, and a rest on the result. */
const MORPH_AT = BOT_TURNS.length;
const MORPH_BEATS = BOT_TURNS.length + 1 + FLUX_TURNS.length + 2;

export function Comparison() {
  const ref = useRef<HTMLElement>(null);
  const [beat, setBeat] = useTicker(ref, 1300, MORPH_BEATS);
  const flux = beat >= MORPH_AT;
  const melting = beat === MORPH_AT;
  const botShown = BOT_TURNS.slice(0, Math.min(beat + 1, BOT_TURNS.length));
  const fluxShown = FLUX_TURNS.slice(0, Math.max(0, beat - MORPH_AT));
  /* Without motion nothing advances, so a side is shown as it ends. */
  const replay = (side: "bot" | "flux") =>
    setBeat(reducedMotion() ? (side === "bot" ? MORPH_AT - 1 : MORPH_BEATS - 1) : side === "bot" ? 0 : MORPH_AT);
  return (
    <section ref={ref} className={"morph" + (flux ? " is-flux" : "") + (melting ? " melting" : "")} aria-label="Diferencial">
      <div className="morph-head">
        <h2 className="block-title">Por que não é mais um chatbot.</h2>
        <div className="morph-switch" role="group" aria-label="Ver de novo">
          <button type="button" className={!flux ? "on" : undefined} onClick={() => replay("bot")}>
            Bot tradicional
          </button>
          <button type="button" className={flux ? "on" : undefined} onClick={() => replay("flux")}>
            Fluxvia
          </button>
        </div>
      </div>

      <div className="morph-stage">
        <ul className="traits traits-l">
          {COMPARE.slice(0, 3).map((r, i) => (
            <Trait key={r.label} r={r} i={i} />
          ))}
        </ul>

        <div className="phone" aria-hidden>
          <div className="phone-head">
            <span className="phone-name phone-name-bot">
              <span className="bot-av">B</span>Atendimento automático
            </span>
            <span className="phone-name phone-name-flux">
              <Logo size={20} />
            </span>
          </div>
          <div className="phone-body">
            {!flux || melting
              ? botShown.map(([who, text], i) =>
                  text === "menu" ? (
                    <div key={i} className="msg msg-in pm bot-menu">
                      Olá! Digite o número da opção:
                      <span className="menu-btn">1. Agendamento</span>
                      <span className="menu-btn">2. Valores</span>
                      <span className="menu-btn">3. Falar com atendente</span>
                    </div>
                  ) : (
                    <div key={i} className={`msg pm ${who === "user" ? "msg-out" : "msg-in bot-said"}`}>
                      {text}
                    </div>
                  ),
                )
              : fluxShown.map(([who, text], i) => (
                  <div key={`f${i}`} className={`msg pm ${who === "user" ? "msg-out" : "msg-in flux-said"}`}>
                    {text}
                  </div>
                ))}
            {flux && !melting && fluxShown.length === FLUX_TURNS.length && (
              <div className="booked">
                <span className="done-chk">
                  <Check size={11} />
                </span>
                Agenda do Rafael: sexta, 10h
              </div>
            )}
          </div>
          <div className="phone-input">{flux ? "Escreva do seu jeito" : "Digite 1, 2 ou 3"}</div>
        </div>

        <ul className="traits traits-r">
          {COMPARE.slice(3).map((r, i) => (
            <Trait key={r.label} r={r} i={i + 3} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function Trait({ r, i }: { r: (typeof COMPARE)[number]; i: number }) {
  return (
    <li className="trait" style={{ "--i": i } as CSSProperties}>
      <span className="trait-label">{r.label}</span>
      <span className="trait-swap">
        <span className="trait-old">{r.old}</span>
        <span className="trait-new">
          <Check size={13} />
          {r.neu}
        </span>
      </span>
    </li>
  );
}

/* ================= one conversation, three situations =====================
   Instead of three cards, one conversation that keeps going: it wakes a client
   up, answers a price from the catalogue, recognises a limit, and hands over
   to the team, where the switch moves to Manual and a person answers. The
   note beside it names each situation as it happens. It plays again after a
   pause while it stays on screen. */

const CASES = [
  {
    tag: "Reativação",
    title: "Sua base inativa virou pipeline.",
    bullets: [
      "Separa quem não volta há semanas e não tem horário marcado",
      "Aborda no WhatsApp com o tom da sua marca",
      "Conduz até um horário real, sem você intervir",
      "Mostra quantos voltaram por campanha",
    ],
  },
  {
    tag: "Serviços e preços",
    title: "Catálogo certinho, sem inventar valor.",
    bullets: [
      "Responde com os valores reais do seu sistema",
      "Mostra opções de pacote e plano",
      "Não dá desconto sem autorização",
      "Conduz pra agenda quando faz sentido",
    ],
  },
  {
    tag: "Passa pra equipe",
    title: "Quando precisa de alguém, a conversa chega resumida pra sua equipe.",
    bullets: [
      "Detecta convênio, desconto e parcelamento",
      "Não negocia condição fora da tabela",
      "Resume a conversa pra equipe ler em 5 segundos",
      "Junta pedidos do mesmo cliente num único pedido pra equipe",
    ],
  },
];
const TALK_AT = [300, 1500, 2700, 5200, 6300, 7300, 9800, 11000, 13000, 14200, 15300, 16500];

export function Problems() {
  const ref = useRef<HTMLElement>(null);
  useTimeline(ref, TALK_AT, { threshold: 0.35, loop: 5000 });
  return (
    <section ref={ref} className="talk" id="resolve" aria-label="O que a Fluxvia resolve">
      <div className="talk-head">
        <h2 className="block-title">Três coisas que todo cliente manda pelo WhatsApp.</h2>
        <p className="block-sub">
          Cada atendimento pode consultar agenda, serviços e regras do seu sistema, sem menu de
          botões.
        </p>
      </div>
      <div className="talk-stage">
        <div className="talk-notes">
          {CASES.map((c, i) => (
            <div key={c.tag} className={`talk-note talk-note-${i + 1}`}>
              <div className="case-tag">{c.tag}</div>
              <h3 className="case-title">{c.title}</h3>
              <ul className="case-list">
                {c.bullets.map((b) => (
                  <li key={b}>
                    <Check size={12} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="card talk-chat" aria-hidden>
          <div className="talk-bar">
            <span className="chat-av">C</span>
            <span className="chat-who">
              <b>Camila Rocha</b>
              <span>pelo WhatsApp</span>
            </span>
            <span className="mode">
              <span className="mode-thumb" />
              <span className="mode-opt">Automático</span>
              <span className="mode-opt">Manual</span>
            </span>
          </div>
          <div className="talk-body">
            <div className="tm tm-1"><div className="msg msg-out">faz tempo que não te vejo, posso encaixar essa semana?</div></div>
            <div className="tm tm-2"><div className="msg msg-in">pode ser quinta de manhã?</div></div>
            <div className="tm tm-3"><div className="msg msg-out">Quinta às 9h com a Ana está livre. Marquei pra você.</div></div>
            <div className="tm tm-4"><div className="msg msg-in">quanto custa avaliação?</div></div>
            <div className="tm tm-5"><div className="lookup">Consultando serviços e preços</div></div>
            <div className="tm tm-6"><div className="msg msg-out">Pela tabela da clínica, a avaliação é R$ 150 e já inclui o retorno.</div></div>
            <div className="tm tm-7"><div className="msg msg-in">atendem Unimed?</div></div>
            <div className="tm tm-8"><div className="msg msg-out">Convênio quem confirma é a equipe. Já passo a conversa pra eles, resumida.</div></div>
            <div className="tm tm-9"><div className="msg msg-in">Queria falar sobre um exame.</div></div>
            <div className="tm tm-10"><div className="msg msg-out">Claro. Já chamo alguém da equipe pra você.</div></div>
            <div className="tm tm-11"><div className="handoff">A conversa passou para a equipe</div></div>
            <div className="tm tm-12"><div className="msg msg-team"><b>Paula</b>Oi! Aqui é a Paula, pode me contar.</div></div>
          </div>
        </div>
      </div>
      <p className="for-line">
        Se o seu cliente manda mensagem todo dia, isso é pra você. Clínicas e fisioterapia,
        barbearias e estética, academias e pilates, spas e estúdios, consultórios e profissionais
        autônomos, salões e nail bars, corretoras.
      </p>
    </section>
  );
}

/* ================= what it plugs into =====================================
   The Fluxvia in the middle, and what the business already has arrives one
   piece at a time as the thing itself: a spreadsheet as a small table, the
   WhatsApp as a conversation, the calendar as slots, the clinic system as
   structured data. Each docks against the Fluxvia and becomes part of it,
   starting with the spreadsheet, which is enough on day one. */

const PIECES = [
  { key: "sheet", name: "Planilha de clientes", desc: "Excel ou CSV" },
  { key: "wa", name: "WhatsApp", desc: "Pela integração oficial da Meta" },
  { key: "cal", name: "Google Agenda", desc: "A agenda de cada profissional" },
  { key: "sys", name: "Sistema da clínica (ATOS)", desc: "Agenda e cadastro" },
  { key: "custom", name: "Integração sob medida", desc: "Para o sistema que você já usa" },
];
const DOCK_AT = [300, 1300, 2300, 3300, 4300];

function PieceBody({ kind }: { kind: string }) {
  if (kind === "sheet")
    return (
      <div className="pc-sheet">
        <span>Nome</span><span>Última visita</span>
        <span>Roberto Lima</span><span>há 120 dias</span>
        <span>Marina Alves</span><span>há 94 dias</span>
      </div>
    );
  if (kind === "wa")
    return (
      <div className="pc-wa">
        <span className="pc-bubble in">Oi, tem horário sábado?</span>
        <span className="pc-bubble out">Tenho às 10h. Marco?</span>
      </div>
    );
  if (kind === "cal")
    return (
      <div className="pc-cal">
        <span>09:00</span><i className="busy" />
        <span>10:00</span><i />
        <span>11:00</span><i className="busy" />
      </div>
    );
  if (kind === "sys")
    return (
      <dl className="pc-sys">
        <dt>Paciente</dt><dd>Marina Alves</dd>
        <dt>Convênio</dt><dd>Particular</dd>
        <dt>Retorno</dt><dd>sexta, 10:00</dd>
      </dl>
    );
  return <div className="pc-custom">API, webhook ou arquivo</div>;
}

export function Integrations() {
  const ref = useRef<HTMLElement>(null);
  useTimeline(ref, DOCK_AT, { threshold: 0.35 });
  return (
    <section ref={ref} className="dock-scene" id="integracoes" aria-label="Integrações">
      <div className="dock-copy">
        <h2 className="block-title">Entra com o que você já tem hoje.</h2>
        <p className="block-sub">
          Uma planilha de clientes já basta para começar. A agenda, o sistema da clínica e o
          WhatsApp entram depois, quando fizer sentido pra você, nesta ordem ou em qualquer outra.
        </p>
      </div>
      <div className="dock">
        <div className="dock-core">
          <Logo size={34} />
        </div>
        {PIECES.map((p, i) => (
          <div key={p.key} className={`piece piece-${p.key}`} style={{ "--i": i } as CSSProperties}>
            <div className="piece-h">
              <b>{p.name}</b>
              <span>{p.desc}</span>
            </div>
            <PieceBody kind={p.key} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= you stay in control ====================================
   Calm on purpose: after all that automation, a still section that says who
   is in charge. The switch is the visitor's to move, not the page's. */

const MODES = [
  { key: "auto", name: "Automático", line: "A Fluxvia e a sua equipe respondem juntas." },
  { key: "manual", name: "Manual", line: "Só a sua equipe responde aquele cliente." },
  { key: "privado", name: "Privado", line: "Só você vê e responde. Pra conversa sensível." },
];

export function Reassurance() {
  const [mode, setMode] = useState(0);
  return (
    <section className="block" id="seguranca" aria-label="Você continua no controle">
      <div className="wrap control">
        <div className="control-copy">
          <h2 className="block-title">Você continua no controle.</h2>
          <p className="block-sub">
            A Fluxvia reativa, qualifica e encaminha casos sensíveis pra equipe.{" "}
            <strong>Não dá diagnóstico, não prescreve, não negocia preço por conta própria.</strong>{" "}
            Cada cliente fala com uma pessoa quando precisa.
          </p>
          <ul className="control-points">
            <li><strong>Sua equipe sempre pode assumir.</strong> Um clique passa a conversa pro time.</li>
            <li><strong>Histórico auditável.</strong> Cada conversa, agendamento e atribuição fica registrado.</li>
            <li><strong>Limites configuráveis.</strong> Você define quais audiências a Fluxvia pode tocar e com que frequência.</li>
          </ul>
        </div>
        <div className="switch">
          <div className="switch-label" id="switch-label">Quem responde esta conversa</div>
          <div className="seg" role="group" aria-labelledby="switch-label" style={{ ["--m-i" as string]: mode }}>
            <span className="seg-thumb" aria-hidden />
            {MODES.map((m, i) => (
              <button
                key={m.key}
                type="button"
                aria-pressed={mode === i}
                className={"seg-opt" + (mode === i ? " on" : "")}
                onClick={() => setMode(i)}
              >
                {m.name}
              </button>
            ))}
          </div>
          {/* The live region stays mounted; only its content is replaced, which
              is what screen readers announce. */}
          <p className="switch-line" aria-live="polite">
            <span key={mode} className="switch-line-in">{MODES[mode].line}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/** What the section closes with, under the two meters. */
const PRICING_POINTS: Array<{ lead: string; rest: string }> = [
  { lead: "Sem fidelidade", rest: "cancela quando quiser, sem multa e sem aviso prévio." },
  {
    lead: "Seu WhatsApp nunca para de receber mensagens",
    rest: "se o saldo acabar, avisamos na hora e as mensagens continuam chegando.",
  },
  {
    lead: "Você vê cada centavo no extrato",
    rest: "ação por ação, com o nome do cliente que foi atendido.",
  },
];

/** No count of attendances here, and that is a correction rather than a
 *  simplification: the unit charged is one reply, and a reply that books costs
 *  more than a standard one, so any count would be a ceiling the reception only
 *  reaches by never booking. `app/offer.py` owns the numbers; the page states the
 *  amount, its deadline and the price of each action. */
export function Pricing() {
  return (
    <section className="block block-tint" id="planos" aria-label="Como cobramos">
      <div className="wrap">
        <div className="block-head">
          <h2 className="block-title">Uma mensalidade fixa, e o resto só quando a Fluxvia trabalha.</h2>
          <p className="block-sub">
            Duas contas separadas, e nenhuma delas te surpreende: a assinatura não muda de um mês
            para o outro, e o saldo só anda quando alguém é atendido.
          </p>
        </div>

        <div className="meters">
          <div className="meter meter-plan">
            <div className="meter-k">A plataforma</div>
            <div className="amount">
              <span className="amount-cur">R$</span>
              <span className="amount-n">157</span>
              <span className="amount-c">,00</span>
              <span className="amount-per">por mês</span>
            </div>
            <div className="meter-plan-name">Plano Beta Fechado</div>
            <div className="seats">
              <span className="seat on" />
              <span className="seat on" />
              <span className="seat" />
              <span className="seats-t">
                <b>2 pessoas incluídas</b> · R$ 47,00 por pessoa a mais
              </span>
            </div>
            <p className="meter-note">
              O nome é do plano, não da porta: a entrada está aberta e você cria sua conta agora.
              Painel, histórico, integrações, atualizações e suporte. Sem teto de uso: a Fluxvia
              atende o quanto precisar, e isso não mexe na mensalidade.
            </p>
          </div>

          <div className="meter meter-balance">
            <div className="meter-k">O trabalho</div>
            <div className="amount">
              <span className="amount-cur">R$</span>
              <span className="amount-n">32</span>
              <span className="amount-c">,00</span>
              <span className="amount-per">por nossa conta</span>
            </div>
            <div className="meter-legend">
              <b>Entram quando você confirma seu e-mail</b> e valem por 21 dias, o tempo de ver a
              recepção trabalhando antes de decidir qualquer coisa.
            </div>
            <div className="rate">
              <span className="rate-v">R$ 0,32</span>
              <span className="rate-k">cada resposta da recepção</span>
            </div>
            <div className="rate">
              <span className="rate-v">R$ 0,95</span>
              <span className="rate-k">quando a resposta marca, remarca ou cancela</span>
            </div>
            <p className="meter-note">
              Depois da cortesia você recarrega quando quiser, no valor que quiser. Mês parado, saldo
              intacto.
            </p>
          </div>
        </div>

        <div className="points">
          {PRICING_POINTS.map((p) => (
            <div key={p.lead} className="point">
              <Check size={16} />
              <span>
                <strong>{p.lead}</strong> {p.rest}
              </span>
            </div>
          ))}
        </div>

        <p className="pricing-foot">
          O que a Meta cobrar pelas mensagens do WhatsApp é à parte, direto com ela. A
          mensalidade acima é da Fluxvia.
        </p>
      </div>
    </section>
  );
}

/* ================= the close ==============================================
   The call to act beside the operation that goes on without anyone: the
   day's feed keeps arriving next to the button, a quiet reminder of what the
   visitor is signing up for. Compact, so at the end of the page it and the
   footer share the last screen. */

const CLOSE_FEED: Array<[string, string]> = [
  ["Marina Alves remarcou para sexta, 10h", "remarcou"],
  ["Bruno Castro recebeu a vaga de amanhã, 14h", "agendou"],
  ["Roberto Lima agendou quinta, 9h", "agendou"],
  ["Ana Costa confirmou amanhã, 10h", "confirmou"],
  ["Júlia Prado perguntou o valor e recebeu a tabela", "respondeu"],
  ["Lucas Rocha remarcou para segunda", "remarcou"],
  ["Patrícia Nunes agendou sexta, 16h", "agendou"],
  ["Tomás Reis confirmou hoje, 18h", "confirmou"],
];

export function SceneClose() {
  const ref = useRef<HTMLElement>(null);
  const [tick] = useTicker(ref, 2200, 400);
  const shown = Array.from({ length: 4 }, (_, k) => {
    const n = tick + 3 - k;
    return { n, rec: CLOSE_FEED[n % CLOSE_FEED.length] };
  });
  return (
    <section ref={ref} className="close" id="final-cta" aria-label="Criar conta">
      <div className="close-copy">
        <h2 className="final-title">Faça sua base voltar a conversar.</h2>
        <p className="final-sub">
          A conta é sua em dois minutos, sem cartão: 21 dias de teste e R$ 32,00 de crédito quando
          você confirmar o e-mail. Se alguém que já usa te indicou, é só chegar com o link dela.
        </p>
        <a className="cta-primary" href={SIGNUP_URL}>
          Criar minha conta
          <Arrow />
        </a>
      </div>
      <div className="close-feed" aria-hidden>
        <div className="op-h">
          <b>Hoje</b>
          <span>feito pela Fluxvia, agora</span>
        </div>
        {shown.map(({ n, rec }) => (
          <div key={n} className={`op-rec k-${rec[1]}`}>
            <span className="op-dot" />
            {rec[0]}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================= FOOTER ================= */

export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-grid">
          <div>
            <Logo size={26} />
            <p className="footer-tagline">Atendimento inteligente conectado ao seu negócio.</p>
          </div>
          <div>
            <div className="footer-h">Produto</div>
            <a href="#produto">Como funciona</a>
            <a href="#resolve">Casos de uso</a>
            <a href="#integracoes">Integrações</a>
            <a href="#planos">Planos</a>
          </div>
          <div>
            <div className="footer-h">Empresa</div>
            <a href="#empresa">Sobre</a>
            <a href="#empresa">Contato</a>
          </div>
          <div>
            <div className="footer-h">Legal</div>
            <a href="/sua-carteira.html">Sua carteira</a>
            <a href="/termos.html">Termos</a>
            <a href="/privacidade.html">Privacidade</a>
            <a href="/privacidade.html">LGPD</a>
            <a href="/suporte.html">Suporte</a>
          </div>
        </div>
        <div id="empresa" className="footer-business">
          <div className="footer-h">Dados da empresa</div>
          <p className="footer-business-line">
            Wilson Moraes dos Santos Suporte e Desenvolvimento em Tecnologia LTDA
            <span aria-hidden="true"> · </span>
            <span className="mono">CNPJ 50.139.751/0001-61</span>
          </p>
          <p className="footer-business-line">
            Rua S7, 881, Apt 303 — Set Bela Vista, Goiânia/GO —{" "}
            <span className="mono">CEP 74823-415</span> — Brasil
          </p>
          <p className="footer-business-line">
            <a href="mailto:contato@fluxvia.com.br">contato@fluxvia.com.br</a>
            <span aria-hidden="true"> · </span>
            <a href="tel:+5562998027881">+55 62 99802-7881</a>
          </p>
        </div>
        <div className="footer-bottom">
          <span>© 2026 Fluxvia · Feito no Brasil</span>
          <span className="footer-ver">v0.1 · MVP</span>
        </div>
      </div>
    </footer>
  );
}
